import { flexRender } from '@tanstack/react-table'
import { ArrowDown, ArrowUp } from 'lucide-react'
import React from 'react'
import { cn } from '@/lib/utils'

export default function CallLogsTable({ table, isRefreshing }) {
  'use no memo'
  return (
    <table className="w-full divide-y divide-gray-200 border-b border-light">
      <thead
        className={`
          sticky top-0 z-20 bg-white outline-2 outline-white outline-solid
          after:absolute after:right-0 after:bottom-0 after:left-0 after:h-px
          after:bg-gray-200 after:content-['']
        `}
      >
        <tr>
          {table.getHeaderGroups().map(headerGroup => (
            headerGroup.headers.map(header => (
              <th
                key={header.id}
                className={`
                  relative h-8 flex-nowrap text-center font-taurus text-2xs
                  font-medium tracking-wider whitespace-nowrap text-gray-500
                  uppercase
                `}
                colSpan={header.colSpan}
                style={{ width: `${header.getSize()}px` }}
              >
                {header.isPlaceholder
                  ? null
                  : (
                      <div
                        {...{
                          className: header.column.getCanSort() ? 'cursor-pointer select-none flex items-center justify-center gap-2' : '',
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {{
                          desc: <ArrowDown size={12} />,
                          asc: <ArrowUp size={12} />,
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    )}
                {header.column.getCanResize() && (
                  <div
                    {...{
                      onMouseDown: header.getResizeHandler(),
                      onTouchStart: header.getResizeHandler(),
                      className: cn(
                        `
                          absolute bottom-0 my-0.5 h-full w-[2px]
                          cursor-col-resize touch-none transition-opacity
                          select-none
                          group-hover:opacity-80
                        `,
                        table.options.columnResizeDirection,
                        header.column.getIsResizing() ? 'isResizing' : '',
                        table.options.columnResizeDirection === 'ltr'
                          ? `right-0`
                          : `left-0`,
                        header.column.getIsResizing()
                          ? `bg-primary-500 opacity-100`
                          : `bg-black/20 opacity-0`,
                        table.options.columnResizeMode === 'onEnd' && header.column.getIsResizing()
                          ? `
                            translate-x-[${
                  (table.options.columnResizeDirection === 'rtl' ? -1 : 1)
                  * (table.getState().columnSizingInfo.deltaOffset ?? 0)
                  }px]
                          `
                          : '',
                      ),
                    }}
                  />
                )}
              </th>
            ))
          ))}
        </tr>
      </thead>
      <tbody className={cn(`
        divide-y divide-gray-200 border-x border-light bg-white
      `, { 'animate-pulse': isRefreshing })}
      >
        {table.getRowModel().rows.map(row => (
          <React.Fragment key={row.id}>
            <tr
              className={cn(`
                relative cursor-pointer divide-x divide-gray-50 text-center
                transition-colors duration-100
                hover:bg-gray-50
              `, {
                'bg-gray-100': row.getIsSelected(),
                'bg-primary-50/50 hover:bg-primary-50': row.depth === 1,
              })}
              onClick={row.getToggleSelectedHandler()}
            >
              {row.getVisibleCells().map(cell => (
                <td
                  key={cell.id}
                  className={`
                    relative h-11 text-tiny whitespace-nowrap text-gray-500
                  `}
                >
                  {flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext(),
                  )}
                </td>
              ))}
            </tr>
          </React.Fragment>
        ))}
      </tbody>
    </table>

  )
}
